import React, { useState, useEffect, useRef, useCallback } from 'react';

import './translations';
import { useTranslation } from 'react-i18next';
import { getAuthHeaders, API_BASE_URL } from './auth';

// MUI components
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import TextField from '@mui/material/TextField';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import LinearProgress from '@mui/material/LinearProgress';
import Alert from '@mui/material/Alert';
import Snackbar from '@mui/material/Snackbar';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import InputLabel from '@mui/material/InputLabel';
import FormControl from '@mui/material/FormControl';
import IconButton from '@mui/material/IconButton';
import Dialog from '@mui/material/Dialog';
import CloseIcon from '@mui/icons-material/Close';
import { modalHeaderBtnSx } from './chatMessageStyles';
import CircularProgress from '@mui/material/CircularProgress';
import Tooltip from '@mui/material/Tooltip';
import InputAdornment from '@mui/material/InputAdornment';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import MicIcon from '@mui/icons-material/Mic';
import StopIcon from '@mui/icons-material/Stop';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import EditIcon from '@mui/icons-material/Edit';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';

// Shared hooks and components
import VoiceOverlay from './components/VoiceOverlay';
import useProcessingSound from './hooks/useProcessingSound';
import useAudioPlaybackQueue from './hooks/useAudioPlaybackQueue';
import useSpeechToText from './hooks/useSpeechToText';
import { computeRMS, getSupportedMimeType } from './utils/audioUtils';


function formatNoteDate(value, locale) {
    if (!value) return '';
    const parsed = new Date(value);
    if (isNaN(parsed.getTime())) return value;
    return parsed.toLocaleDateString(locale, { day: '2-digit', month: 'short', year: 'numeric' });
}

/** A single labelled field inside a saved-note card. */
function NoteField({ label, value }) {
    if (!value) return null;
    return (
        <Box>
            <Typography sx={{ color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.04em', fontSize: '10px', fontWeight: 600 }}>
                {label}
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.primary', fontSize: '13px', lineHeight: 1.55, whiteSpace: 'pre-wrap' }}>
                {value}
            </Typography>
        </Box>
    );
}

/** A saved customer visit rendered as a modern card. */
function VisitNoteCard({ visit, t, locale, onEdit, onDelete }) {
    const dateLabel = formatNoteDate(visit.date, locale);
    return (
        <Box
            onClick={onEdit}
            sx={{
                p: 2,
                borderRadius: '12px',
                border: '1px solid',
                borderColor: 'divider',
                bgcolor: 'background.paper',
                cursor: 'pointer',
                transition: 'border-color 0.2s ease, background-color 0.2s ease',
                '&:hover': { borderColor: 'primary.main', bgcolor: 'action.hover' },
            }}
        >
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 1.25 }}>
                <Typography sx={{ fontWeight: 600, color: 'primary.main', fontSize: '14px', letterSpacing: '0.01em' }}>
                    {visit.client_name || '—'}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
                    {visit.satisfaction_rating && (
                        <Chip
                            size="small"
                            label={`★ ${visit.satisfaction_rating}/5`}
                            sx={{ height: 20, fontSize: '11px', fontWeight: 600, bgcolor: 'rgba(25, 208, 239, 0.12)', color: 'primary.main' }}
                        />
                    )}
                    {dateLabel && <Typography variant="caption" sx={{ color: 'text.secondary' }}>{dateLabel}</Typography>}
                    <IconButton onClick={(e) => { e.stopPropagation(); onEdit(); }} size="small" sx={{ color: 'text.secondary', '&:hover': { color: 'primary.main' } }}>
                        <EditIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                    <IconButton onClick={(e) => { e.stopPropagation(); onDelete(); }} size="small" sx={{ color: 'text.secondary', '&:hover': { color: 'error.main' } }}>
                        <DeleteOutlineIcon sx={{ fontSize: 17 }} />
                    </IconButton>
                </Box>
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <NoteField label={t('dc-topics') || 'Topics Discussed'} value={visit.topics_discussed} />
                <NoteField label={t('dc-feedback') || 'Customer Feedback'} value={visit.customer_feedback} />
                <NoteField label={t('dc-decisions') || 'Decisions Made'} value={visit.decisions_made} />
            </Box>
        </Box>
    );
}

/** Edit dialog for a saved note — same fields and styling as the manual input form. */
function EditNoteDialog({ note, clients, t, onClose, onSaved }) {
    const [date, setDate] = useState(note.date || '');
    const [clientId, setClientId] = useState(note.client_id != null ? String(note.client_id) : '');
    const [topics, setTopics] = useState(note.topics_discussed || '');
    const [feedback, setFeedback] = useState(note.customer_feedback || '');
    const [decisions, setDecisions] = useState(note.decisions_made || '');
    const [satisfaction, setSatisfaction] = useState(note.satisfaction_rating || '');
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');

    const handleSave = async () => {
        setSaving(true);
        setError('');
        try {
            const res = await fetch(`${API_BASE_URL}/api/customer_visits/${note.id}`, {
                method: 'PUT',
                headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    client_id: parseInt(clientId, 10),
                    date,
                    topics_discussed: topics,
                    customer_feedback: feedback,
                    decisions_made: decisions,
                    satisfaction_rating: satisfaction,
                }),
            });
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
        } catch (e) {
            setSaving(false);
            setError(t('dc-save-error') || 'Could not save the visit. Please try again.');
            return;
        }
        setSaving(false);
        onSaved();
    };

    return (
        <Dialog
            open
            onClose={onClose}
            maxWidth="sm"
            fullWidth
            sx={{ '& .MuiDialog-container': { alignItems: 'center', justifyContent: 'center' } }}
            PaperProps={{ sx: { borderRadius: '14px' } }}
        >
            <Box sx={{ p: 2.5, display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Typography sx={{ fontWeight: 600, fontSize: '16px' }}>{t('dc-edit-note') || 'Edit note'}</Typography>
                    <IconButton onClick={onClose} size="small" sx={{ color: 'text.secondary', '&:hover': { color: 'primary.main' } }}>
                        <CloseIcon fontSize="small" />
                    </IconButton>
                </Box>

                <TextField
                    label={t('date')}
                    type="date"
                    value={date}
                    onChange={e => setDate(e.target.value)}
                    size="small"
                    InputLabelProps={{ shrink: true }}
                    sx={{ maxWidth: 220, '& input::-webkit-calendar-picker-indicator': { filter: theme => theme.palette.mode === 'dark' ? 'invert(1)' : 'none' } }}
                />

                <FormControl size="small" fullWidth>
                    <InputLabel>{t('client')}</InputLabel>
                    <Select value={clientId} onChange={e => setClientId(e.target.value)} label={t('client')}>
                        {clients.map(c => (
                            <MenuItem key={c.id} value={String(c.id)}>{c.first_name} {c.last_name}</MenuItem>
                        ))}
                    </Select>
                </FormControl>

                <TextField
                    label={`${t('dc-topics') || 'Topics Discussed'} *`}
                    value={topics}
                    onChange={e => setTopics(e.target.value)}
                    multiline
                    rows={3}
                    size="small"
                    fullWidth
                />
                <TextField
                    label={t('dc-feedback') || 'Customer Feedback'}
                    value={feedback}
                    onChange={e => setFeedback(e.target.value)}
                    multiline
                    rows={2}
                    size="small"
                    fullWidth
                />
                <TextField
                    label={t('dc-decisions') || 'Decisions Made'}
                    value={decisions}
                    onChange={e => setDecisions(e.target.value)}
                    multiline
                    rows={2}
                    size="small"
                    fullWidth
                />

                <FormControl size="small" sx={{ maxWidth: 220 }}>
                    <InputLabel>{t('dc-satisfaction') || 'Satisfaction Rating'}</InputLabel>
                    <Select value={satisfaction} onChange={e => setSatisfaction(e.target.value)} label={t('dc-satisfaction') || 'Satisfaction Rating'}>
                        <MenuItem value="">—</MenuItem>
                        <MenuItem value="1">1</MenuItem>
                        <MenuItem value="2">2</MenuItem>
                        <MenuItem value="3">3</MenuItem>
                        <MenuItem value="4">4</MenuItem>
                        <MenuItem value="5">5</MenuItem>
                    </Select>
                </FormControl>

                {error && <Alert severity="error">{error}</Alert>}

                <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', mt: 0.5 }}>
                    <Button onClick={onClose} sx={{ textTransform: 'none' }}>{t('dc-cancel') || 'Cancel'}</Button>
                    <Button
                        variant="contained"
                        onClick={handleSave}
                        disabled={!topics.trim() || !clientId || saving}
                        startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}
                        sx={{ textTransform: 'none', fontWeight: 600 }}
                    >
                        {t('save')}
                    </Button>
                </Box>
            </Box>
        </Dialog>
    );
}

function ExpandableClient({ title, icon, expanded, sessionId, language, modal, onClose }) {
    const today = new Date().toISOString().slice(0, 10);
    const { t, i18n } = useTranslation();

    // ── Tab state ──
    const [isOpen, setIsOpen] = useState(false);
    const [selectedTab, setSelectedTab] = useState('voice');

    // ── Client list state ──
    const [clients, setClients] = useState([]);

    // ── Manual input state ──
    const [date, setDate] = useState(today);
    const [client, setClient] = useState('');
    const [topicsDiscussed, setTopicsDiscussed] = useState('');
    const [customerFeedback, setCustomerFeedback] = useState('');
    const [decisionsMade, setDecisionsMade] = useState('');
    const [satisfactionRating, setSatisfactionRating] = useState('');
    const [manualSaving, setManualSaving] = useState(false);
    const [manualSaveMessage, setManualSaveMessage] = useState('');

    // ── All-notes tab state ──
    const [notes, setNotes] = useState([]);
    const [notesLoading, setNotesLoading] = useState(false);
    const [notesError, setNotesError] = useState('');
    const [notesFilter, setNotesFilter] = useState('');
    const [editingNote, setEditingNote] = useState(null);
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [deleting, setDeleting] = useState(false);
    const [noteSnackbar, setNoteSnackbar] = useState({ open: false, severity: 'success', message: '' });
    const dateInputRef = useRef(null);

    // ── Voice agent state ──
    const [voiceState, setVoiceState] = useState('idle'); // idle | initializing | listening | thinking | speaking
    const [dataCollectionStatus, setDataCollectionStatus] = useState(null); // null | collecting | confirming | completed
    const [collectedData, setCollectedData] = useState({});
    const [currentField, setCurrentField] = useState(null);
    const [fieldIndex, setFieldIndex] = useState(0);
    const [totalFields, setTotalFields] = useState(0);
    const [savedId, setSavedId] = useState(null);
    const [autoPrefill, setAutoPrefill] = useState(true);

    // ── Voice refs (audio / VAD / TTS) ──
    const mediaStreamRef = useRef(null);
    const audioContextRef = useRef(null);
    const analyserRef = useRef(null);
    const mediaRecorderRef = useRef(null);
    const audioChunksRef = useRef([]);
    const ttsAudioRef = useRef(null);
    const ttsAudioContextRef = useRef(null);
    const vadFrameIdRef = useRef(null);
    const speechStartTimeRef = useRef(null);
    const silenceStartTimeRef = useRef(null);
    const voiceStoppedByUserRef = useRef(false);
    const dataCollectionSessionIdRef = useRef(null);
    // Set once the visit is saved, so the queue-empty handler closes the modal
    // instead of restarting listening (see point f — leave after saving).
    const sessionCompletedRef = useRef(false);
    const onCloseRef = useRef(null);
    const stopVoiceAgentRef = useRef(null);

    // Audio queue for streaming TTS playback
    const botResponseRef = useRef('');
    const startListeningRef = useRef(null);
    const abortControllerRef = useRef(null);
    const consecutiveFailuresRef = useRef(0);
    const MAX_CONSECUTIVE_FAILURES = 3;

    // VAD settings
    const VAD_THRESHOLD = 0.025;
    const MIN_SPEECH_MS = 400;
    // 2s (not 1s): a 1s pause cut speakers off mid-sentence and shipped a
    // partial transcript, making the assistant re-ask. See AdvisorVoiceDialog.js.
    const SILENCE_MS = 2000;

    const [audioLevel, setAudioLevel] = useState(0);

    // ── Shared hooks ──
    const { processingPulse } = useProcessingSound(voiceState);

    // ── Manual-field speech-to-text (dictation into the free-text fields) ──
    const stt = useSpeechToText({
        onResult: useCallback((field, text) => {
            // setState functions are stable, so an inline map is safe here.
            const setters = {
                topics: setTopicsDiscussed,
                feedback: setCustomerFeedback,
                decisions: setDecisionsMade,
            };
            const setter = setters[field];
            if (setter) {
                setter(prev => (prev && prev.trim() ? `${prev.trim()} ${text}` : text));
            }
        }, []),
    });

    const renderMicButton = (field) => {
        const isRec = stt.recordingField === field;
        const isBusy = stt.transcribingField === field;
        const label = isRec ? (t('dc-mic-stop') || 'Stop recording') : (t('dc-mic-record') || 'Dictate');
        return (
            <Tooltip title={label}>
                <span style={{ position: 'absolute', top: 6, right: 6, zIndex: 1 }}>
                    <IconButton
                        size="small"
                        onClick={() => stt.toggle(field)}
                        disabled={isBusy || (stt.recordingField && stt.recordingField !== field)}
                        aria-label={label}
                    >
                        {isBusy
                            ? <CircularProgress size={18} />
                            : isRec
                                ? <StopIcon fontSize="small" color="error" />
                                : <MicIcon fontSize="small" />}
                    </IconButton>
                </span>
            </Tooltip>
        );
    };

    // ── Locale-aware date display (follows the app language, not the OS) ──
    // A native <input type="date"> always renders in the browser/OS locale, so
    // we show a formatted, read-only value and keep the native picker hidden
    // behind a calendar button. The stored value stays ISO (YYYY-MM-DD).
    const dateLocaleMap = { de: 'de-CH', en: 'en-GB', fr: 'fr-CH', it: 'it-CH', es: 'es-ES' };
    const langBase = (i18n.language || 'de').split('-')[0];
    const dateLocale = dateLocaleMap[langBase] || i18n.language || 'de-CH';

    const formatDateForLocale = (isoDate) => {
        if (!isoDate) return '';
        const [y, m, d] = isoDate.split('-').map(Number);
        const dt = new Date(y, m - 1, d);
        if (isNaN(dt.getTime())) return isoDate;
        return new Intl.DateTimeFormat(dateLocale, { day: '2-digit', month: '2-digit', year: 'numeric' }).format(dt);
    };

    const openDatePicker = () => {
        const el = dateInputRef.current;
        if (!el) return;
        if (typeof el.showPicker === 'function') {
            try { el.showPicker(); return; } catch (_) { /* fall through to focus */ }
        }
        el.focus();
        el.click();
    };

    // ── Voice-agent field display (point a: localized labels, not dev names) ──
    // Maps backend schema field names to existing i18n keys so the progress line
    // and the collected-data chips show proper, translated labels.
    const FIELD_LABEL_KEYS = {
        client_name: 'dc-client-name',
        date: 'dc-visit-date',
        topics_discussed: 'dc-topics',
        customer_feedback: 'dc-feedback',
        decisions_made: 'dc-decisions',
        satisfaction_rating: 'dc-satisfaction',
    };

    const fieldLabel = (name) => {
        if (!name) return '';
        const key = FIELD_LABEL_KEYS[name];
        const translated = key ? t(key) : '';
        if (translated && translated !== key) return translated;
        // Fallback: turn a raw dev name like "client_name" into "Client name".
        return name.replace(/_/g, ' ').replace(/^\w/, (c) => c.toUpperCase());
    };

    // Point b: show the collected date in the app-language European format.
    const formatFieldValue = (name, value) => {
        if (value === null || value === undefined || value === '') return '—';
        if (name === 'date' && /^\d{4}-\d{2}-\d{2}$/.test(String(value))) {
            return formatDateForLocale(value);
        }
        return value;
    };

    const handleQueueEmpty = useCallback(() => {
        // After the save confirmation finishes playing, close ONLY the voice agent
        // (back to the idle success view) and keep the customer-note modal open.
        if (sessionCompletedRef.current) {
            sessionCompletedRef.current = false;
            if (stopVoiceAgentRef.current) stopVoiceAgentRef.current();
            return;
        }
        if (!voiceStoppedByUserRef.current) {
            botResponseRef.current = '';
            if (startListeningRef.current) {
                startListeningRef.current();
            }
        }
    }, []);

    // Keep the latest onClose in a ref so the memoised queue-empty handler can
    // reach it without being re-created.
    useEffect(() => { onCloseRef.current = onClose; }, [onClose]);

    const handlePlaying = useCallback(() => {
        setVoiceState('speaking');
    }, []);

    const {
        enqueueAudio,
        markStreamDone,
        stopPlayback,
        resetQueue,
        audioQueueRef,
        isPlayingRef,
        streamDoneRef,
    } = useAudioPlaybackQueue({
        onQueueEmpty: handleQueueEmpty,
        onPlaying: handlePlaying,
    });

    // ── Load clients ──
    useEffect(() => {
        fetch(`${API_BASE_URL}/api/clients`, { headers: getAuthHeaders() })
            .then(res => res.json())
            .then(data => {
                setClients(data);
                if (data.length > 0) setClient(String(data[0].id));
            })
            .catch(err => console.error('Failed to load clients:', err));
    }, []);

    // ── Load all saved notes (used by the "all notes" tab, refreshed after edits) ──
    const loadNotes = useCallback(() => {
        setNotesLoading(true);
        setNotesError('');
        return fetch(`${API_BASE_URL}/api/customer_visits`, { headers: getAuthHeaders() })
            .then(res => { if (!res.ok) throw new Error(`HTTP ${res.status}`); return res.json(); })
            .then(data => setNotes(Array.isArray(data) ? data : []))
            .catch(() => setNotesError(t('dc-notes-error') || 'Notes could not be loaded.'))
            .finally(() => setNotesLoading(false));
    }, [t]);

    useEffect(() => {
        if (selectedTab === 'all') loadNotes();
    }, [selectedTab, loadNotes]);

    const handleDeleteNote = async () => {
        if (!deleteTarget) return;
        setDeleting(true);
        try {
            const res = await fetch(`${API_BASE_URL}/api/customer_visits/${deleteTarget.id}`, {
                method: 'DELETE',
                headers: getAuthHeaders(),
            });
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            setDeleteTarget(null);
            await loadNotes();
            setNoteSnackbar({ open: true, severity: 'success', message: t('dc-deleted-success') || 'Note deleted.' });
        } catch (e) {
            // Leave the dialog open on failure; the list stays unchanged.
            setNoteSnackbar({ open: true, severity: 'error', message: t('dc-delete-error') || 'Could not delete the note. Please try again.' });
        } finally {
            setDeleting(false);
        }
    };

    // ── Expand / collapse ──
    const toggle = () => setIsOpen(!isOpen);
    useEffect(() => { setIsOpen(expanded); }, [expanded]);

    // ── Manual save ──
    const handleManualSave = async () => {
        setManualSaving(true);
        setManualSaveMessage('');
        try {
            const res = await fetch(`${API_BASE_URL}/api/customer_visits`, {
                method: 'POST',
                headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    client_id: parseInt(client, 10),
                    date,
                    topics_discussed: topicsDiscussed,
                    customer_feedback: customerFeedback,
                    decisions_made: decisionsMade,
                    satisfaction_rating: satisfactionRating,
                }),
            });
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
        } catch (err) {
            setManualSaving(false);
            setManualSaveMessage(t('dc-save-error') || 'Could not save the visit. Please try again.');
            return;
        }
        setManualSaving(false);
        setTopicsDiscussed('');
        setCustomerFeedback('');
        setDecisionsMade('');
        setSatisfactionRating('');
        setDate(today);
        // Point f: leave the page after saving (when shown as a modal).
        if (onClose) {
            onClose();
            return;
        }
        setManualSaveMessage(t('dc-manual-saved') || 'Visit saved successfully.');
        setTimeout(() => setManualSaveMessage(''), 3000);
    };

    // ========================================================================
    // VOICE AGENT LOGIC
    // ========================================================================

    // ── Send voice to data-collection backend ──
    const sendVoiceToBackend = useCallback(async (audioBlob) => {
        setVoiceState('thinking');
        if (abortControllerRef.current) abortControllerRef.current.abort();
        const abortController = new AbortController();
        abortControllerRef.current = abortController;

        // Hard-stop any audio still playing from the previous turn (resetQueue only
        // cleared the queue and left the current source playing → a new playback
        // chain would overlap it, producing two offset voices).
        stopPlayback();
        botResponseRef.current = '';

        const mimeToExt = {
            'audio/webm': 'webm', 'audio/webm;codecs=opus': 'webm',
            'audio/mp4': 'mp4', 'audio/ogg': 'ogg', 'audio/ogg;codecs=opus': 'ogg', 'audio/wav': 'wav',
        };
        const ext = mimeToExt[audioBlob.type] || 'webm';
        const formData = new FormData();
        formData.append('audio', audioBlob, `recording.${ext}`);
        formData.append('session_id', dataCollectionSessionIdRef.current);

        try {
            const res = await fetch(`${API_BASE_URL}/api/voice_data_collection`, {
                method: 'POST',
                headers: getAuthHeaders(),
                body: formData,
                signal: abortController.signal,
            });
            if (!res.ok) throw new Error(`HTTP ${res.status}`);

            const reader = res.body.getReader();
            const decoder = new TextDecoder();
            let buffer = '';

            while (true) {
                if (abortController.signal.aborted) { reader.cancel(); break; }
                const { done, value } = await reader.read();
                if (done) break;
                buffer += decoder.decode(value, { stream: true });
                const events = buffer.split('\n\n');
                buffer = events.pop() || '';

                for (const event of events) {
                    if (abortController.signal.aborted) break;
                    if (!event.trim()) continue;
                    let eventType = '', eventData = '';
                    for (const line of event.split('\n')) {
                        if (line.startsWith('event: ')) eventType = line.slice(7).trim();
                        else if (line.startsWith('data: ')) eventData = line.slice(6);
                    }
                    if (!eventType || !eventData) continue;
                    try {
                        const data = JSON.parse(eventData);
                        if (eventType === 'transcript' && data.text) {
                            consecutiveFailuresRef.current = 0;
                        } else if (eventType === 'text' && data.chunk) {
                            botResponseRef.current += data.chunk;
                        } else if (eventType === 'audio' && data.audio) {
                            enqueueAudio(data);
                        } else if (eventType === 'state') {
                            setDataCollectionStatus(data.status);
                            setCollectedData(data.collected_data || {});
                            setCurrentField(data.current_field || null);
                            setFieldIndex(data.field_index ?? 0);
                            setTotalFields(data.total_fields ?? 0);
                            if (data.saved_id) setSavedId(data.saved_id);
                            if (data.status === 'completed') sessionCompletedRef.current = true;
                        } else if (eventType === 'done') {
                            botResponseRef.current = data.full_response || '';
                            markStreamDone();
                            if (data.saved_id) setSavedId(data.saved_id);
                            if (data.reason === 'empty_transcript' || (!data.full_response && audioQueueRef.current.length === 0)) {
                                consecutiveFailuresRef.current++;
                                if (consecutiveFailuresRef.current >= MAX_CONSECUTIVE_FAILURES) {
                                    consecutiveFailuresRef.current = 0;
                                    stopVoiceAgent();
                                    return;
                                }
                                if (startListeningRef.current && !voiceStoppedByUserRef.current) {
                                    if (mediaStreamRef.current) { mediaStreamRef.current.getTracks().forEach(t => t.stop()); mediaStreamRef.current = null; }
                                    if (audioContextRef.current) { audioContextRef.current.close().catch(() => {}); audioContextRef.current = null; }
                                    setTimeout(() => { if (!voiceStoppedByUserRef.current) startListeningRef.current(); }, 500);
                                }
                                return;
                            }
                            consecutiveFailuresRef.current = 0;
                            if (sessionCompletedRef.current) {
                                // Saved: stop ONLY the voice agent (show the idle success
                                // view) and keep the modal open. If confirmation audio is
                                // still playing, handleQueueEmpty does this once it ends.
                                if (audioQueueRef.current.length === 0 && !isPlayingRef.current) {
                                    sessionCompletedRef.current = false;
                                    stopVoiceAgent();
                                }
                            } else if (audioQueueRef.current.length === 0 && !isPlayingRef.current) {
                                botResponseRef.current = '';
                                if (startListeningRef.current && !voiceStoppedByUserRef.current) startListeningRef.current();
                            }
                        } else if (eventType === 'error') {
                            console.error('Data collection stream error:', data.message);
                            setVoiceState('idle');
                        }
                    } catch (e) { console.warn('Failed to parse SSE data:', eventData, e); }
                }
            }
        } catch (err) {
            if (err.name === 'AbortError') { botResponseRef.current = ''; return; }
            console.error('Voice data collection request failed:', err);
            consecutiveFailuresRef.current++;
            if (consecutiveFailuresRef.current >= MAX_CONSECUTIVE_FAILURES) {
                consecutiveFailuresRef.current = 0;
                stopVoiceAgent();
                return;
            }
            if (startListeningRef.current && !voiceStoppedByUserRef.current) {
                if (mediaStreamRef.current) { mediaStreamRef.current.getTracks().forEach(t => t.stop()); mediaStreamRef.current = null; }
                if (audioContextRef.current) { audioContextRef.current.close().catch(() => {}); audioContextRef.current = null; }
                setTimeout(() => { if (!voiceStoppedByUserRef.current) startListeningRef.current(); }, 500);
            } else {
                setVoiceState('idle');
            }
        } finally {
            if (abortControllerRef.current?.signal.aborted === false) abortControllerRef.current = null;
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [enqueueAudio, markStreamDone, stopPlayback]);

    // ── Start listening (VAD-based) ──
    const startListening = useCallback(async () => {
        try {
            let stream = mediaStreamRef.current;
            let audioCtx = audioContextRef.current;
            let analyser = analyserRef.current;

            if (!stream || stream.getTracks().every(t => t.readyState === 'ended')) {
                stream = await navigator.mediaDevices.getUserMedia({ audio: true });
                mediaStreamRef.current = stream;
                if (audioCtx) audioCtx.close().catch(() => {});
                audioCtx = new (window.AudioContext || window.webkitAudioContext)();
                audioContextRef.current = audioCtx;
                const source = audioCtx.createMediaStreamSource(stream);
                analyser = audioCtx.createAnalyser();
                analyser.fftSize = 512;
                source.connect(analyser);
                analyserRef.current = analyser;
            } else {
                if (audioCtx && audioCtx.state === 'suspended') await audioCtx.resume();
            }

            const mimeType = getSupportedMimeType();
            const recorderOptions = mimeType ? { mimeType } : {};
            const recorder = new MediaRecorder(stream, recorderOptions);
            mediaRecorderRef.current = recorder;
            audioChunksRef.current = [];

            recorder.ondataavailable = (e) => { if (e.data.size > 0) audioChunksRef.current.push(e.data); };
            recorder.onstop = () => {
                if (voiceStoppedByUserRef.current) { audioChunksRef.current = []; return; }
                if (audioChunksRef.current.length > 0) {
                    const actualMimeType = recorder.mimeType || 'audio/webm';
                    const blob = new Blob(audioChunksRef.current, { type: actualMimeType });
                    audioChunksRef.current = [];
                    const speechDuration = speechStartTimeRef.current ? Date.now() - speechStartTimeRef.current : 0;
                    if (blob.size < 3000 || speechDuration < 500) {
                        if (startListeningRef.current && !voiceStoppedByUserRef.current) startListeningRef.current();
                        return;
                    }
                    sendVoiceToBackend(blob);
                }
            };

            recorder.start(100);
            setVoiceState('listening');
            speechStartTimeRef.current = null;
            silenceStartTimeRef.current = null;

            const vadLoop = () => {
                if (!analyserRef.current) return;
                const rms = computeRMS(analyserRef.current);
                setAudioLevel(Math.min(rms * 10, 1));
                const now = Date.now();
                if (rms > VAD_THRESHOLD) {
                    silenceStartTimeRef.current = null;
                    if (!speechStartTimeRef.current) speechStartTimeRef.current = now;
                } else {
                    if (speechStartTimeRef.current && (now - speechStartTimeRef.current) >= MIN_SPEECH_MS) {
                        if (!silenceStartTimeRef.current) silenceStartTimeRef.current = now;
                        else if ((now - silenceStartTimeRef.current) >= SILENCE_MS) {
                            if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') mediaRecorderRef.current.stop();
                            return;
                        }
                    }
                }
                vadFrameIdRef.current = requestAnimationFrame(vadLoop);
            };
            vadFrameIdRef.current = requestAnimationFrame(vadLoop);
        } catch (err) {
            console.error('Microphone access denied:', err);
            setVoiceState('idle');
        }
    }, [sendVoiceToBackend]);

    useEffect(() => { startListeningRef.current = startListening; }, [startListening]);

    // VAD loop for listening state
    useEffect(() => {
        if (voiceState !== 'listening' || !analyserRef.current || !mediaRecorderRef.current) return;
        if (mediaRecorderRef.current.state !== 'recording') return;
        const vadLoop = () => {
            if (!analyserRef.current || voiceState !== 'listening') return;
            const rms = computeRMS(analyserRef.current);
            setAudioLevel(Math.min(rms * 10, 1));
            const now = Date.now();
            if (rms > VAD_THRESHOLD) {
                silenceStartTimeRef.current = null;
                if (!speechStartTimeRef.current) speechStartTimeRef.current = now;
            } else {
                if (speechStartTimeRef.current && (now - speechStartTimeRef.current) >= MIN_SPEECH_MS) {
                    if (!silenceStartTimeRef.current) silenceStartTimeRef.current = now;
                    else if ((now - silenceStartTimeRef.current) >= SILENCE_MS) {
                        if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') mediaRecorderRef.current.stop();
                        return;
                    }
                }
            }
            vadFrameIdRef.current = requestAnimationFrame(vadLoop);
        };
        vadFrameIdRef.current = requestAnimationFrame(vadLoop);
        return () => { if (vadFrameIdRef.current) { cancelAnimationFrame(vadFrameIdRef.current); vadFrameIdRef.current = null; } };
    }, [voiceState]);

    // ── Stop voice agent ──
    const stopVoiceAgent = useCallback(() => {
        voiceStoppedByUserRef.current = true;
        stopPlayback();
        if (abortControllerRef.current) { abortControllerRef.current.abort(); abortControllerRef.current = null; }
        if (vadFrameIdRef.current) { cancelAnimationFrame(vadFrameIdRef.current); vadFrameIdRef.current = null; }
        if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') mediaRecorderRef.current.stop();
        if (mediaStreamRef.current) { mediaStreamRef.current.getTracks().forEach(t => t.stop()); mediaStreamRef.current = null; }
        if (audioContextRef.current) { audioContextRef.current.close().catch(() => {}); audioContextRef.current = null; }
        if (ttsAudioRef.current) {
            if (ttsAudioRef.current.source) { try { ttsAudioRef.current.source.stop(); } catch (e) {} }
            else if (ttsAudioRef.current.pause) ttsAudioRef.current.pause();
            ttsAudioRef.current = null;
        }
        if (ttsAudioContextRef.current) { ttsAudioContextRef.current.close().catch(() => {}); ttsAudioContextRef.current = null; }
        resetQueue();
        audioChunksRef.current = [];
        setAudioLevel(0);
        setVoiceState('idle');
        setDataCollectionStatus(null);
        setCollectedData({});
        setCurrentField(null);
        setFieldIndex(0);
        setTotalFields(0);
        // NOTE: savedId is intentionally NOT cleared here so that after a save the
        // normal idle start screen shows a success banner (keyed on savedId) in the
        // still-open modal. It is reset when a new session starts (startVoiceAgent).
        // On cancel/error savedId is already null, so nothing changes there.
        dataCollectionSessionIdRef.current = null;
        sessionCompletedRef.current = false;
    }, [stopPlayback, resetQueue]);

    // Expose the latest stopVoiceAgent to the memoised queue-empty handler.
    useEffect(() => { stopVoiceAgentRef.current = stopVoiceAgent; }, [stopVoiceAgent]);

    // Hard-cut the voice session if this component unmounts for ANY reason while
    // active (closing the modal, navigating away) — mic, audio and stream all stop.
    useEffect(() => {
        return () => { if (stopVoiceAgentRef.current) stopVoiceAgentRef.current(); };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // ── Barge-in (stop speaking, start listening again) ──
    const stopAndSpeakAgain = useCallback(() => {
        if (abortControllerRef.current) { abortControllerRef.current.abort(); abortControllerRef.current = null; }
        if (ttsAudioRef.current) {
            if (ttsAudioRef.current.source) { try { ttsAudioRef.current.source.stop(); } catch (e) {} }
            else if (ttsAudioRef.current.pause) ttsAudioRef.current.pause();
            ttsAudioRef.current = null;
        }
        stopPlayback();
        speechStartTimeRef.current = null;
        silenceStartTimeRef.current = null;
        startListening();
    }, [startListening, stopPlayback]);

    // ── Start the voice data-collection session ──
    const startVoiceAgent = useCallback(async () => {
        voiceStoppedByUserRef.current = false;
        sessionCompletedRef.current = false;
        setVoiceState('initializing');
        setDataCollectionStatus(null);
        setCollectedData({});
        setCurrentField(null);
        setSavedId(null);

        const dcSessionId = `dc_${Date.now()}_${Math.floor(Math.random() * 100000)}`;
        dataCollectionSessionIdRef.current = dcSessionId;

        const langCode = (language && language.language) ? language.language : 'de';

        try {
            // Unlock audio playback
            const silentAudio = new Audio('data:audio/wav;base64,UklGRigAAABXQVZFZm10IBIAAAABAAEARKwAAIhYAQACABAAAABkYXRhAgAAAAEA');
            silentAudio.volume = 0;
            silentAudio.play().then(() => silentAudio.pause()).catch(() => {});
            if (!ttsAudioContextRef.current || ttsAudioContextRef.current.state === 'closed') {
                ttsAudioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
            }
            if (ttsAudioContextRef.current.state === 'suspended') ttsAudioContextRef.current.resume().catch(() => {});

            // Get mic permission
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            mediaStreamRef.current = stream;
            if (audioContextRef.current) audioContextRef.current.close().catch(() => {});
            const audioCtx = new (window.AudioContext || window.webkitAudioContext)();
            audioContextRef.current = audioCtx;
            const source = audioCtx.createMediaStreamSource(stream);
            const analyser = audioCtx.createAnalyser();
            analyser.fftSize = 512;
            source.connect(analyser);
            analyserRef.current = analyser;

            // Call /start endpoint to get the introduction + first question
            resetQueue();
            botResponseRef.current = '';

            setVoiceState('thinking');

            const res = await fetch(`${API_BASE_URL}/api/voice_data_collection/start`, {
                method: 'POST',
                headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    schema_name: 'customer_visit',
                    session_id: dcSessionId,
                    client_id: parseInt(client, 10) || 1,
                    language: langCode,
                    auto_prefill: autoPrefill,
                }),
            });

            if (!res.ok) throw new Error(`HTTP ${res.status}`);

            const reader = res.body.getReader();
            const decoder = new TextDecoder();
            let buffer = '';

            while (true) {
                const { done, value } = await reader.read();
                if (done) break;
                buffer += decoder.decode(value, { stream: true });
                const sseEvents = buffer.split('\n\n');
                buffer = sseEvents.pop() || '';
                for (const event of sseEvents) {
                    if (!event.trim()) continue;
                    let eventType = '', eventData = '';
                    for (const line of event.split('\n')) {
                        if (line.startsWith('event: ')) eventType = line.slice(7).trim();
                        else if (line.startsWith('data: ')) eventData = line.slice(6);
                    }
                    if (!eventType || !eventData) continue;
                    try {
                        const data = JSON.parse(eventData);
                        if (eventType === 'audio' && data.audio) {
                            enqueueAudio(data);
                        } else if (eventType === 'state') {
                            setDataCollectionStatus(data.status);
                            setCollectedData(data.collected_data || {});
                            setCurrentField(data.current_field || null);
                            setFieldIndex(data.field_index ?? 0);
                            setTotalFields(data.total_fields ?? 0);
                        } else if (eventType === 'done') {
                            markStreamDone();
                            if (audioQueueRef.current.length === 0 && !isPlayingRef.current) {
                                if (startListeningRef.current) startListeningRef.current();
                            }
                        } else if (eventType === 'error') {
                            console.error('Start data collection error:', data.message);
                            stopVoiceAgent();
                        }
                    } catch (e) { console.warn('Failed to parse SSE:', eventData, e); }
                }
            }
        } catch (err) {
            console.error('Failed to start voice data collection:', err);
            setVoiceState('idle');
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [language, enqueueAudio, markStreamDone, resetQueue, startListening, stopVoiceAgent, autoPrefill, client]);

    // ── Progress bar helper ──
    const progressPercent = totalFields > 0 ? Math.round(((fieldIndex) / totalFields) * 100) : 0;

    const filteredNotes = notesFilter
        ? notes.filter(n => String(n.client_id) === notesFilter)
        : notes;

    // ========================================================================
    // RENDER
    // ========================================================================

    return (
        <Accordion
            expanded={modal ? true : isOpen}
            onChange={modal ? undefined : toggle}
            sx={modal
                ? {
                    m: 0,
                    background: 'transparent',
                    border: 'none',
                    boxShadow: 'none',
                    backdropFilter: 'none',
                    borderRadius: 0,
                    '&:before': { display: 'none' },
                    '&:hover': { background: 'transparent', borderColor: 'transparent', boxShadow: 'none' },
                    '&.Mui-expanded': { background: 'transparent', border: 'none', boxShadow: 'none', margin: 0 },
                  }
                : { mb: 2 }}
        >
            <AccordionSummary
                expandIcon={modal ? null : <ExpandMoreIcon sx={{ color: 'text.secondary' }} />}
                sx={{
                    '& .MuiAccordionSummary-content': {
                        alignItems: 'center',
                        gap: 1.5,
                        // In the modal: no vertical margins, just an 8px
                        // padding-bottom — matching the unified modal header.
                        my: modal ? 0 : '10px',
                        '&.Mui-expanded': { my: modal ? 0 : '10px' },
                    },
                    minHeight: modal ? 'auto' : 48,
                    '&.Mui-expanded': { minHeight: modal ? 'auto' : 48 },
                    px: 0,
                    ...(modal && { pt: 0, pb: 1, cursor: 'default' }),
                }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: 32,
                        height: 32,
                        borderRadius: '8px',
                        bgcolor: 'rgba(25, 208, 239, 0.08)',
                        color: 'primary.main',
                        '& .MuiSvgIcon-root': { fontSize: 18 },
                    }}
                >
                    {icon}
                </Box>
                <Typography sx={{ fontWeight: 400, fontSize: '15px', letterSpacing: '0.02em', flexGrow: 1 }}>{title}</Typography>
                {modal && onClose && (
                    <IconButton
                        onClick={() => { stopVoiceAgent(); onClose(); }}
                        onMouseDown={(e) => e.stopPropagation()}
                        size="small"
                        aria-label="Close"
                        sx={{ ...modalHeaderBtnSx, p: 0.75 }}
                    >
                        <CloseIcon fontSize="small" />
                    </IconButton>
                )}
            </AccordionSummary>

            <AccordionDetails sx={{ pt: 0, px: 0, pb: 0 }}>
                {/* Tab header */}
                <Tabs
                    value={selectedTab}
                    onChange={(_, val) => setSelectedTab(val)}
                    sx={{
                        px: 0,
                        '& .MuiTab-root': { textTransform: 'none', fontWeight: 500 },
                        '& .MuiTab-root:first-of-type': { pl: 0 },
                    }}
                >
                    <Tab label={t('dc-tab-voice') || 'Voice Agent'} value="voice" />
                    <Tab label={t('dc-tab-manual') || 'Manual Input'} value="manual" />
                    <Tab label={t('dc-tab-all') || 'All Notes'} value="all" />
                </Tabs>

                {/* ──────── VOICE AGENT TAB ──────── */}
                {selectedTab === 'voice' && (
                    <Box
                        className={voiceState !== 'idle' ? 'voice-active' : ''}
                        sx={{ position: 'relative', px: 0, pt: 2, pb: 0, ...(voiceState !== 'idle' && { minHeight: { xs: 500, md: 700 } }) }}
                    >
                        {voiceState === 'idle' && (
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2, pb: 0 }}>
                                {/* After a save: same start screen, with a success banner on top
                                    (in the app's teal/primary palette, not the default green). */}
                                {savedId && (
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 1,
                                            px: 1.5,
                                            py: 1,
                                            borderRadius: '10px',
                                            bgcolor: 'rgba(25, 208, 239, 0.08)',
                                            border: '1px solid',
                                            borderColor: 'rgba(25, 208, 239, 0.3)',
                                            color: 'primary.main',
                                        }}
                                    >
                                        <CheckCircleOutlineIcon sx={{ fontSize: 18 }} />
                                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                            {t('dc-saved-success') || 'Customer visit saved successfully!'} (ID: {savedId})
                                        </Typography>
                                    </Box>
                                )}
                                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                    {t('dc-voice-description') || 'Start the voice agent to record a customer visit step by step. The AI will guide you through all fields.'}
                                </Typography>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={autoPrefill}
                                            onChange={(e) => setAutoPrefill(e.target.checked)}
                                            size="small"
                                        />
                                    }
                                    label={t('dc-auto-prefill') || 'Auto-prefill from calendar'}
                                    sx={{ '& .MuiFormControlLabel-label': { fontSize: '0.875rem' } }}
                                />
                                <Button
                                    variant="contained"
                                    onClick={startVoiceAgent}
                                    startIcon={
                                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                            <path d="M12 3V21M8 8V16M4 10V14M16 8V16M20 10V14" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                                        </svg>
                                    }
                                    sx={{ textTransform: 'none', fontWeight: 600, alignSelf: 'flex-start' }}
                                >
                                    {t('dc-start-voice') || 'Start Voice Agent'}
                                </Button>
                            </Box>
                        )}

                        {/* Voice overlay using shared VoiceOverlay component */}
                        {voiceState !== 'idle' && (
                            <VoiceOverlay
                                voiceState={voiceState}
                                audioLevel={audioLevel}
                                processingPulse={processingPulse}
                                onStop={stopVoiceAgent}
                                onBargeIn={stopAndSpeakAgain}
                            >
                                {/* Progress indicator */}
                                {dataCollectionStatus && (
                                    <Box sx={{ width: '80%', maxWidth: 320, zIndex: 11 }}>
                                        <LinearProgress
                                            variant="determinate"
                                            value={dataCollectionStatus === 'completed' ? 100 : progressPercent}
                                            sx={{
                                                height: 6,
                                                borderRadius: 3,
                                                bgcolor: 'rgba(255,255,255,0.15)',
                                                '& .MuiLinearProgress-bar': { borderRadius: 3 },
                                            }}
                                        />
                                        <Typography variant="caption" sx={{ color: 'text.secondary', mt: 0.5, display: 'block', textAlign: 'center' }}>
                                            {dataCollectionStatus === 'confirming'
                                                ? (t('dc-confirming') || 'Confirming...')
                                                : dataCollectionStatus === 'completed'
                                                    ? (t('dc-completed') || 'Completed!')
                                                    : `${t('dc-field') || 'Field'} ${fieldIndex + 1} / ${totalFields}`
                                            }
                                            {currentField && dataCollectionStatus === 'collecting' && (
                                                <span> — {fieldLabel(currentField)}</span>
                                            )}
                                        </Typography>
                                    </Box>
                                )}

                                {/* Collected data chips – each sized to its own content and
                                    wrapping naturally. Long values wrap onto multiple lines
                                    within the chip instead of forcing a horizontal scrollbar. */}
                                {Object.keys(collectedData).length > 0 && (
                                    <Box sx={{
                                        display: 'flex',
                                        flexWrap: 'wrap',
                                        justifyContent: 'center',
                                        alignItems: 'flex-start',
                                        gap: 0.75,
                                        zIndex: 11,
                                        px: 2,
                                        width: '100%',
                                        maxWidth: 640,
                                    }}>
                                        {Object.entries(collectedData).map(([key, val]) => (
                                            <Chip
                                                key={key}
                                                label={`${fieldLabel(key)}: ${formatFieldValue(key, val)}`}
                                                size="small"
                                                sx={{
                                                    height: 'auto',
                                                    maxWidth: '100%',
                                                    bgcolor: 'rgba(255,255,255,0.12)',
                                                    color: 'text.primary',
                                                    fontSize: '0.7rem',
                                                    '& .MuiChip-label': {
                                                        whiteSpace: 'normal',
                                                        py: 0.5,
                                                        lineHeight: 1.35,
                                                    },
                                                }}
                                            />
                                        ))}
                                    </Box>
                                )}
                            </VoiceOverlay>
                        )}
                    </Box>
                )}

                {/* ──────── MANUAL INPUT TAB ──────── */}
                {selectedTab === 'manual' && (
                    <Box sx={{ px: 0, pt: 2, pb: 0, display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <Box sx={{ position: 'relative', maxWidth: 220 }}>
                            <TextField
                                label={t('date')}
                                value={formatDateForLocale(date)}
                                onClick={openDatePicker}
                                size="small"
                                fullWidth
                                InputLabelProps={{ shrink: true }}
                                InputProps={{
                                    readOnly: true,
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <IconButton
                                                size="small"
                                                edge="end"
                                                onClick={(e) => { e.stopPropagation(); openDatePicker(); }}
                                                aria-label={t('date')}
                                            >
                                                <CalendarTodayIcon fontSize="small" />
                                            </IconButton>
                                        </InputAdornment>
                                    ),
                                }}
                                sx={{ '& .MuiInputBase-input': { cursor: 'pointer' } }}
                            />
                            {/* Hidden native picker: holds the ISO value and provides the OS calendar UI */}
                            <input
                                ref={dateInputRef}
                                type="date"
                                value={date}
                                onChange={e => setDate(e.target.value)}
                                tabIndex={-1}
                                aria-hidden="true"
                                style={{ position: 'absolute', left: 12, bottom: 0, width: 1, height: 1, opacity: 0, pointerEvents: 'none', border: 0, padding: 0 }}
                            />
                        </Box>

                        <FormControl size="small" fullWidth>
                            <InputLabel>{t('client')}</InputLabel>
                            <Select
                                value={client}
                                onChange={e => setClient(e.target.value)}
                                label={t('client')}
                            >
                                {clients.map(c => (
                                    <MenuItem key={c.id} value={String(c.id)}>{c.first_name} {c.last_name}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        <Box sx={{ position: 'relative' }}>
                            <TextField
                                label={`${t('dc-topics') || 'Topics Discussed'} *`}
                                value={topicsDiscussed}
                                onChange={e => setTopicsDiscussed(e.target.value)}
                                multiline
                                rows={3}
                                size="small"
                                fullWidth
                                sx={{ '& textarea': { pr: 3.5 } }}
                            />
                            {renderMicButton('topics')}
                        </Box>

                        <Box sx={{ position: 'relative' }}>
                            <TextField
                                label={t('dc-feedback') || 'Customer Feedback'}
                                value={customerFeedback}
                                onChange={e => setCustomerFeedback(e.target.value)}
                                multiline
                                rows={2}
                                size="small"
                                fullWidth
                                sx={{ '& textarea': { pr: 3.5 } }}
                            />
                            {renderMicButton('feedback')}
                        </Box>

                        <Box sx={{ position: 'relative' }}>
                            <TextField
                                label={t('dc-decisions') || 'Decisions Made'}
                                value={decisionsMade}
                                onChange={e => setDecisionsMade(e.target.value)}
                                multiline
                                rows={2}
                                size="small"
                                fullWidth
                                sx={{ '& textarea': { pr: 3.5 } }}
                            />
                            {renderMicButton('decisions')}
                        </Box>

                        <FormControl size="small" sx={{ maxWidth: 220 }}>
                            <InputLabel>{t('dc-satisfaction') || 'Satisfaction Rating'}</InputLabel>
                            <Select
                                value={satisfactionRating}
                                onChange={e => setSatisfactionRating(e.target.value)}
                                label={t('dc-satisfaction') || 'Satisfaction Rating'}
                            >
                                <MenuItem value="">—</MenuItem>
                                <MenuItem value="1">1</MenuItem>
                                <MenuItem value="2">2</MenuItem>
                                <MenuItem value="3">3</MenuItem>
                                <MenuItem value="4">4</MenuItem>
                                <MenuItem value="5">5</MenuItem>
                            </Select>
                        </FormControl>

                        <Button
                            variant="contained"
                            onClick={handleManualSave}
                            disabled={!topicsDiscussed.trim() || !client || manualSaving}
                            sx={{ alignSelf: 'flex-start', textTransform: 'none', fontWeight: 600 }}
                        >
                            {manualSaving ? (t('dc-saving') || 'Saving...') : (t('save'))}
                        </Button>

                        {manualSaveMessage && (
                            <Alert severity="success" sx={{ mt: 1 }}>
                                {manualSaveMessage}
                            </Alert>
                        )}
                    </Box>
                )}

                {/* ──────── ALL NOTES TAB ──────── */}
                {selectedTab === 'all' && (
                    <Box sx={{ p: 0, paddingTop: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <FormControl size="small" sx={{ maxWidth: 260 }}>
                            <InputLabel>{t('dc-filter-client') || 'Filter by client'}</InputLabel>
                            <Select
                                value={notesFilter}
                                onChange={e => setNotesFilter(e.target.value)}
                                label={t('dc-filter-client') || 'Filter by client'}
                            >
                                <MenuItem value="">{t('dc-all-clients') || 'All clients'}</MenuItem>
                                {clients.map(c => (
                                    <MenuItem key={c.id} value={String(c.id)}>{c.first_name} {c.last_name}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        {notesLoading ? (
                            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                                <CircularProgress size={24} sx={{ color: 'primary.main' }} />
                            </Box>
                        ) : notesError ? (
                            <Alert severity="error">{notesError}</Alert>
                        ) : filteredNotes.length === 0 ? (
                            <Typography variant="body2" sx={{ color: 'text.secondary', py: 2, textAlign: 'center' }}>
                                {t('dc-no-notes') || 'No notes found.'}
                            </Typography>
                        ) : (
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, maxHeight: 'calc(92vh - 220px)', overflowY: 'auto', pr: 0.5 }}>
                                {filteredNotes.map(note => (
                                    <VisitNoteCard
                                        key={note.id}
                                        visit={note}
                                        t={t}
                                        locale={i18n.language}
                                        onEdit={() => setEditingNote(note)}
                                        onDelete={() => setDeleteTarget(note)}
                                    />
                                ))}
                            </Box>
                        )}

                        {editingNote && (
                            <EditNoteDialog
                                note={editingNote}
                                clients={clients}
                                t={t}
                                onClose={() => setEditingNote(null)}
                                onSaved={() => {
                                    setEditingNote(null);
                                    loadNotes();
                                    setNoteSnackbar({ open: true, severity: 'success', message: t('dc-updated-success') || 'Note updated.' });
                                }}
                            />
                        )}

                        <Dialog
                            open={!!deleteTarget}
                            onClose={() => { if (!deleting) setDeleteTarget(null); }}
                            maxWidth="xs"
                            fullWidth
                            PaperProps={{ sx: { borderRadius: '14px' } }}
                        >
                            <Box sx={{ p: 2.5 }}>
                                <Typography sx={{ fontWeight: 600, fontSize: '16px', mb: 1 }}>
                                    {t('dc-delete-title') || 'Delete note?'}
                                </Typography>
                                <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
                                    {t('dc-delete-body') || 'This note will be permanently deleted.'}
                                </Typography>
                                <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                                    <Button onClick={() => setDeleteTarget(null)} disabled={deleting} sx={{ textTransform: 'none' }}>
                                        {t('dc-cancel') || 'Cancel'}
                                    </Button>
                                    <Button
                                        variant="contained"
                                        color="error"
                                        onClick={handleDeleteNote}
                                        disabled={deleting}
                                        startIcon={deleting ? <CircularProgress size={16} color="inherit" /> : <DeleteOutlineIcon />}
                                        sx={{ textTransform: 'none', fontWeight: 600 }}
                                    >
                                        {t('dc-delete') || 'Delete'}
                                    </Button>
                                </Box>
                            </Box>
                        </Dialog>

                        <Snackbar
                            open={noteSnackbar.open}
                            autoHideDuration={3000}
                            onClose={() => setNoteSnackbar((s) => ({ ...s, open: false }))}
                            anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                        >
                            <Alert
                                onClose={() => setNoteSnackbar((s) => ({ ...s, open: false }))}
                                severity={noteSnackbar.severity}
                                variant="filled"
                                sx={{
                                    width: '100%',
                                    ...(noteSnackbar.severity === 'success' && {
                                        bgcolor: 'primary.main',
                                        color: 'primary.contrastText',
                                        '& .MuiAlert-icon': { color: 'primary.contrastText' },
                                    }),
                                }}
                            >
                                {noteSnackbar.message}
                            </Alert>
                        </Snackbar>
                    </Box>
                )}
            </AccordionDetails>
        </Accordion>
    );
}

export default ExpandableClient;
